import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableCountryCodeDropdown from '../components/ui/SearchableCountryCodeDropdown';
import InputWithPrepend from '../components/ui/InputWithPrepend';
import { Helmet } from 'react-helmet-async';
import { client } from '../apiHelper';

const OnboardingTrialPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // read optional query param directly from URL bar 
    const orgTypeParam = searchParams.get('organization_type');

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        confirm_password: '',
        business_name: '',
        country_code: '+234', // Default matching your signup page
        phone_number: '',
        btype: '', // To be explicitly chosen by user
        // include in formData state (default to 'O' if not in URL)
        organization_type: (orgTypeParam === 'O' || orgTypeParam === 'R') ? orgTypeParam : 'O',
    });

    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState('idle');

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Business type options available for onboarding
    const businessTypes = [
        { key: 'SRV', title: 'Services', desc: 'Consulting, agencies, freelancers, and professional providers.' },
        { key: 'RTL', title: 'Retail & Commerce', desc: 'Shops, e-commerce, supermarkets, and product distribution.' },
        { key: 'MAN', title: 'Manufacturing', desc: 'Factories, production units, and industrial processing.' },
        { key: 'EDU', title: 'Education', desc: 'Schools, academies, training centers, and e-learning platforms.' },
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSelectBType = (typeKey) => {
        setFormData((prev) => ({ ...prev, btype: typeKey }));
        if (errors.btype) {
            setErrors((prev) => ({ ...prev, btype: '' }));
        }
    };

    const validateForm = () => {
        let newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[\d\s\-\(\)]{8,15}$/;

        if (!formData.username) {
            newErrors.username = 'Email address is required.';
        } else if (!emailRegex.test(formData.username)) {
            newErrors.username = 'Invalid email format.';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required.';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters long.';
        }

        if (formData.password !== formData.confirm_password) {
            newErrors.confirm_password = 'Passwords do not match.';
        }

        if (!formData.business_name) {
            newErrors.business_name = 'Business name is required.';
        }

        if (!formData.phone_number) {
            newErrors.phone_number = 'Phone number is required.';
        } else if (!phoneRegex.test(formData.phone_number)) {
            newErrors.phone_number = 'Invalid phone number format.';
        }

        if (!formData.btype) {
            newErrors.btype = 'Please select a industry/business type.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        setSubmitStatus('loading');
        e.preventDefault();

        if (!validateForm()) {
            setSubmitStatus('error');
            return
        }

        try {
            const fullPhoneNumber = formData.country_code + formData.phone_number.replace(/\s/g, '');
            const response = await client.signup(
                formData.username,
                formData.password,
                formData.business_name,
                formData.phone_number,
                formData.btype,
                formData.organization_type,
            )

            setSubmitStatus('success');
            // LOCAL
            // setTimeout(() => {
            //     if (formData.btype === 'SRV' || formData.btype === 'EDU') {
            //         window.location.href = 'http://localhost:3001';
            //     } else if (formData.btype === 'RTL') {
            //         window.location.href = 'http://localhost:3000';
            //     } else if (formData.btype === 'MAN') {
            //         window.location.href = 'http://localhost:3002';
            //     }
            // }, 2000);
            
            // LIVE
            setTimeout(() => {
                if (formData.btype === 'SRV' || formData.btype === 'EDU') {
                    window.location.href = 'https://services.arxistechnologies.com';
                } else if (formData.btype === 'RTL') {
                    window.location.href = 'https://accounting.arxistechnologies.com';
                } else if (formData.btype === 'MAN') {
                    window.location.href = 'https://mrp.arxistechnologies.com';
                }
            }, 2000);

        } catch (error) {
            console.error(error);
            setSubmitStatus('error');
            const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'An unexpected error occurred during registration.';
            setErrors(prev => ({ ...prev, general: errorMessage }));
        }


        // Note: Passwords generally shouldn't pass via react-router state for security, 
        // but if your flow needs it parsed, you can explicitly add it here.
        // navigate('/signup-business', { state: statePayload });

    };

    const CountryCodePrepend = (
        <SearchableCountryCodeDropdown
            value={formData.country_code}
            onChange={handleChange}
            error={errors.phone_number}
        />
    );

    return (
        <div className="relative flex items-center justify-center min-h-screen bg-gray-50 p-4 lg:p-8">
            <Helmet>
                <title>Start Your 14-Day Free Trial</title>
                <meta name="description" content="Tell us a bit about your organization to personalize your onboarding experience." />
            </Helmet>
            <Card className="z-10 w-full max-w-2xl mx-auto p-8 shadow-xl">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                        Start Your Free Trial
                    </h2>
                    <p className="mt-2 text-sm text-text-secondary">
                        Tell us a bit about your organization to personalize your onboarding experience.
                    </p>
                </div>

                <form onSubmit={handleSubmit} noValidate className="space-y-6">

                    {/* Section 1: Business Profile */}
                    <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100 space-y-4">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                            Business Details
                        </h3>

                        <Input
                            label="Legal Business Name"
                            id="business_name"
                            name="business_name"
                            type="text"
                            placeholder="e.g., Acme Solutions Ltd"
                            value={formData.business_name}
                            onChange={handleChange}
                            required
                            error={errors.business_name}
                        />
                        {errors.business_name && <p className="text-gmail-red text-xs mt-1">{errors.business_name}</p>}

                        <InputWithPrepend
                            label="Business Phone Number"
                            id="phone_number"
                            name="phone_number"
                            type="tel"
                            placeholder="e.g., 801 234 5678"
                            value={formData.phone_number}
                            onChange={handleChange}
                            required
                            error={errors.phone_number}
                            prepend={CountryCodePrepend}
                            className="pl-2"
                        />
                        {errors.phone_number && <p className="text-gmail-red text-xs mt-1">{errors.phone_number}</p>}
                    </div>

                    {/* Section 2: Account Details */}
                    <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100 space-y-4">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                            Administrator Credentials
                        </h3>

                        <Input
                            label="Work Email Address"
                            id="username"
                            name="username"
                            type="email"
                            placeholder="you@email.com"
                            value={formData.username}
                            onChange={handleChange}
                            required
                            error={errors.username}
                        />
                        {errors.username && <p className="text-gmail-red text-xs mt-1">{errors.username}</p>}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Input
                                    label="Password"
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Choose a safe password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    error={errors.password}
                                    trailingIcon={
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="text-gray-500 hover:text-gray-700 focus:outline-none pointer-events-auto"
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                        >
                                            {showPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                                    <path d="M1 1l22 22" />
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                            )}
                                        </button>
                                    }
                                />
                                {errors.password && <p className="text-gmail-red text-xs mt-1">{errors.password}</p>}
                            </div>
                            <div>
                                <Input
                                    label="Confirm Password"
                                    id="confirm_password"
                                    name="confirm_password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Verify password"
                                    value={formData.confirm_password}
                                    onChange={handleChange}
                                    required
                                    error={errors.confirm_password}
                                    trailingIcon={
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="text-gray-500 hover:text-gray-700 focus:outline-none pointer-events-auto"
                                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                        >
                                            {showConfirmPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                                    <path d="M1 1l22 22" />
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                            )}
                                        </button>
                                    }
                                />
                                {errors.confirm_password && <p className="text-gmail-red text-xs mt-1">{errors.confirm_password}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Industry Selection */}
                    <div className="space-y-3">
                        <label className="block text-sm font-medium text-text-primary">
                            Select Your Industry Type <span className="text-gmail-red">*</span>
                        </label>
                        {errors.btype && <p className="text-gmail-red text-xs">{errors.btype}</p>}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {businessTypes.map((type) => {
                                const isSelected = formData.btype === type.key;
                                return (
                                    <button
                                        key={type.key}
                                        type="button"
                                        onClick={() => handleSelectBType(type.key)}
                                        className={`p-4 rounded-lg border-2 text-left transition-all relative ${isSelected
                                            ? 'border-gmail-blue bg-blue-50/40 ring-1 ring-gmail-blue'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className="flex justify-between items-center mb-1">
                                            <h4 className="font-semibold text-gray-900">{type.title}</h4>
                                            {isSelected && (
                                                <span className="h-4 w-4 rounded-full bg-gmail-blue flex items-center justify-center">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-text-secondary leading-normal">{type.desc}</p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <Button
                        type="submit"
                        fullWidth
                        className="py-3 mt-4 text-base font-medium"
                        disabled={submitStatus === 'loading'}
                    >
                        {submitStatus === 'loading' ? 'Processing...' : 'Proceed to Verification & Setup'}
                    </Button>
                    {submitStatus === 'success' && (
                        <p className="text-gmail-green text-sm text-center mb-4 mt-2">
                            Registration successfull! Redirecting to login...
                        </p>
                    )}
                </form>
            </Card>
        </div>
    );
};

export default OnboardingTrialPage;